import React from 'react';
import { ImageLibraryItem } from '@/hooks/runware/image-library';
import { Separator } from '@/components/ui/separator';
import { ImageSettings } from '@/components/image-lightbox/ImageDetail/components/ImageSettings';
import { ChevronLeft, ChevronRight, CloudDownload, Trash2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import GradientButton from '@/components/ui/GradientButton';
import { PublicImageAddToProjectButton } from '@/components/image-generator';
import { FilterInfoDisplay } from '../FilterInfoDisplay';

interface PublicImageMetadataSidebarProps {
  image: ImageLibraryItem;
  isOwner: boolean;
  isDeleteLoading: boolean;
  onDownload: () => void;
  onDelete: () => void;
  hasPrevious?: boolean;
  hasNext?: boolean;
  isLoadingMore?: boolean;
  hasNextPage?: boolean;
  onPrevious?: () => void;
  onNext?: () => void;
  onLoadMore?: () => void;
}

export const PublicImageMetadataSidebar: React.FC<PublicImageMetadataSidebarProps> = ({
  image,
  isOwner,
  isDeleteLoading,
  onDownload,
  onDelete,
  hasPrevious = false,
  hasNext = false,
  isLoadingMore = false,
  hasNextPage = false,
  onPrevious,
  onNext,
  onLoadMore
}) => {
  return (
    <div className="w-full lg:w-[250px] flex-shrink-0 lg:border-l border-accent/10 flex flex-col md:h-full md:max-h-[95vh]">
      {/* Main content container with proper structure */}
      <div className="flex flex-col md:h-full">
        {/* Navigation buttons at the top */}
        <div className="border-b border-accent/10 bg-background">
          <div className="p-3 md:p-4">
            <div className="flex justify-between items-center gap-3 w-full">
              <motion.button
                className={cn(
                  "flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all flex-1 backdrop-blur-sm border",
                  hasPrevious
                    ? "bg-gradient-to-r from-slate-100/80 to-gray-100/80 dark:from-slate-800/80 dark:to-gray-800/80 border-slate-200/50 dark:border-slate-700/50 text-slate-700 dark:text-slate-200 hover:from-slate-200/90 hover:to-gray-200/90 dark:hover:from-slate-700/90 dark:hover:to-gray-700/90 shadow-sm hover:shadow-md"
                    : "bg-slate-50/60 dark:bg-slate-900/60 border-slate-200/30 dark:border-slate-800/30 text-slate-400 dark:text-slate-600 cursor-not-allowed"
                )}
                onClick={hasPrevious && onPrevious ? onPrevious : undefined}
                disabled={!hasPrevious}
                whileHover={hasPrevious ? { scale: 1.02, y: -1 } : {}}
                whileTap={hasPrevious ? { scale: 0.98 } : {}}
                data-test-id="sidebar-prev-button"
              >
                <ChevronLeft size={16} className="flex-shrink-0" />
                <span>Last</span>
              </motion.button>
              
              <motion.button
                className={cn(
                  "flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all flex-1 backdrop-blur-sm border",
                  hasNext
                    ? "bg-gradient-to-r from-slate-800 to-slate-900 dark:from-slate-700 dark:to-slate-800 border-slate-700/50 dark:border-slate-600/50 text-white hover:from-slate-700 hover:to-slate-800 dark:hover:from-slate-600 dark:hover:to-slate-700 shadow-sm hover:shadow-md"
                    : isLoadingMore
                      ? "bg-gradient-to-r from-slate-700 to-slate-800 dark:from-slate-600 dark:to-slate-700 border-slate-600/50 dark:border-slate-500/50 text-white shadow-sm"
                      : "bg-slate-50/60 dark:bg-slate-900/60 border-slate-200/30 dark:border-slate-800/30 text-slate-400 dark:text-slate-600 cursor-not-allowed"
                )}
                onClick={() => {
                  if (hasNext && onNext && !isLoadingMore) {
                    onNext();
                  } 
                  else if (hasNextPage && !isLoadingMore && onLoadMore) {
                    onLoadMore();
                  }
                }}
                disabled={(!hasNext && !hasNextPage) || isLoadingMore}
                whileHover={hasNext && !isLoadingMore ? { scale: 1.02, y: -1 } : {}}
                whileTap={hasNext && !isLoadingMore ? { scale: 0.98 } : {}}
                data-test-id="sidebar-next-button"
                type="button"
              >
                {isLoadingMore ? (
                  <span className="text-sm font-medium bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent animate-pulse">
                    Loading...
                  </span>
                ) : (
                  <>
                    <span>Next</span>
                    <ChevronRight size={16} className="flex-shrink-0" />
                  </>
                )}
              </motion.button>
            </div>
          </div>
        </div>

        {/* Add to Project Button */}
        <div className="border-b border-accent/10 bg-background">
          <div className="px-3 md:px-4 pt-3 md:pt-4 pb-3 md:pb-4">
            <PublicImageAddToProjectButton 
              itemId={image.id}
              dropdownLabel="Add to Project"
              forceWhiteText={false}
              variant="compact"
              gradientFrom="from-purple-50/80 to-violet-50/80 dark:from-purple-950/30 dark:to-violet-950/30"
              gradientTo="hover:from-purple-100/80 hover:to-violet-100/80 dark:hover:from-purple-900/40 dark:hover:to-violet-900/40"
              accentColor="from-purple-400 to-violet-500"
              iconColor="text-purple-600 dark:text-purple-400"
              borderColor="border-purple-200/60 dark:border-purple-700/40"
              hoverBorderColor="hover:border-purple-300/70 dark:hover:border-purple-600/50"
              addText="Add to Project"
              manageText="Add to Project"
              addDescription="Copy to a project"
              manageDescription="Copy to a project"
            />
          </div>
        </div>

        {/* Scrollable content area with filter information */}
        <div className="p-3 md:p-4 md:overflow-y-auto">
          {/* Filter tags at the top */}
          <div>
            <FilterInfoDisplay image={image} />
          </div>
          
          {/* Settings section */}
          <div className="mt-4">
            <ImageSettings image={image} />
          </div>
          
          <Separator className="my-4" />
          
          {/* Delete button only for owned images */}
          <div className="mt-4">
            {isOwner && (
              <GradientButton
                variant="red"
                icon={<Trash2 className="h-4 w-4" />}
                onClick={onDelete}
                isLoading={isDeleteLoading}
                disabled={isDeleteLoading}
                testId="delete-image-button"
                fullWidth
              >
                {isDeleteLoading ? 'Deleting...' : 'Delete'}
              </GradientButton>
            )}
          </div>
        </div>
        
        {/* Fixed bottom section with download button */}
        <div className="border-t border-accent/10 bg-background md:mt-auto">
          <div className="p-3 md:p-4">
            <GradientButton
              variant="blue"
              icon={<CloudDownload className="h-4 w-4" />}
              onClick={onDownload}
              testId="download-image-button"
              fullWidth
            >
              Download
            </GradientButton>
          </div>
        </div>
      </div>
    </div>
  );
}; 